import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { StockAnalogLensData } from '@/mocks/stockAnalogMock';
import { AnalogDailyOutlookTimeline } from './AnalogDailyOutlookTimeline';
import { formatDateShort, formatPct, pctChipClass } from './AnalogLensAccordion';

type HorizonKey = 't5' | 't10' | 't20';

const HORIZONS: { key: HorizonKey; label: string; short: string }[] = [
  { key: 't5', label: '5 sessions', short: '5d' },
  { key: 't10', label: '10 sessions', short: '10d' },
  { key: 't20', label: '20 sessions', short: '20d' },
];

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function barColor(value: number): string {
  if (value > 0.15) return '#059669';
  if (value < -0.15) return '#e11d48';
  return '#9ca3af';
}

type OutlookStats = {
  horizons: {
    key: HorizonKey;
    label: string;
    short: string;
    median: number;
    positive: number;
    total: number;
  }[];
  distribution: {
    date: string;
    value: number;
    headline: string;
    similarity: number;
  }[];
  best: { date: string; value: number; headline: string } | null;
  worst: { date: string; value: number; headline: string } | null;
};

function buildOutlookStats(matches: StockAnalogLensData['matches']): OutlookStats | null {
  if (!matches.length) return null;

  const horizons = HORIZONS.map(({ key, label, short }) => {
    const vals = matches
      .map((m) => m.forwardReturns?.[key])
      .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
    return {
      key,
      label,
      short,
      median: vals.length ? median(vals) : 0,
      positive: vals.filter((v) => v > 0).length,
      total: vals.length,
    };
  }).filter((h) => h.total > 0);

  const distribution = matches
    .map((m) => ({
      date: m.date,
      value: m.forwardReturns?.t5 ?? 0,
      headline: m.headline,
      similarity: m.similarityScore,
    }))
    .filter((d) => typeof d.value === 'number' && !Number.isNaN(d.value))
    .sort((a, b) => b.value - a.value);

  if (!horizons.length && !distribution.length) return null;

  const best = distribution[0] ?? null;
  const worst = distribution[distribution.length - 1] ?? null;

  return { horizons, distribution, best, worst };
}

function MedianBarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: { short: string; median: number; positive: number; total: number } }[];
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-medium text-gray-900">{row.short} median</p>
      <p className={pctChipClass(row.median)}>{formatPct(row.median)}</p>
      <p className="mt-1 text-gray-500">
        {row.positive}/{row.total} analogs positive
      </p>
    </div>
  );
}

export function AnalogLensOutlookVisual({
  lens,
  referenceDate,
  medianBarTitle = 'Median SPY path',
  subjectLabel,
}: {
  lens: StockAnalogLensData;
  referenceDate?: string;
  /** Y-axis chart title for the median horizon bars. */
  medianBarTitle?: string;
  /** Passed through to the session timeline (e.g. ticker vs SPY). */
  subjectLabel?: string;
}) {
  const stats = useMemo(() => buildOutlookStats(lens.matches), [lens.matches]);

  if (!stats) return null;

  const chartData = stats.horizons.map((h) => ({
    name: h.short,
    median: h.median,
    positive: h.positive,
    total: h.total,
    short: h.short,
  }));

  const maxAbs = Math.max(
    ...stats.distribution.map((d) => Math.abs(d.value)),
    ...stats.horizons.map((h) => Math.abs(h.median)),
    1,
  );

  const primaryHorizon = stats.horizons[0];
  const hitRate =
    primaryHorizon && primaryHorizon.total > 0
      ? `${primaryHorizon.positive}/${primaryHorizon.total}`
      : null;

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Forward outlook from {lens.matches.length} analogs
          </p>
          <p className="mt-0.5 text-sm font-medium text-gray-900">
            {hitRate ? (
              <>
                <span className="text-emerald-700">{hitRate}</span> positive at 5d
                {primaryHorizon ? (
                  <span className="text-gray-500">
                    {' '}
                    · median {formatPct(primaryHorizon.median)}
                  </span>
                ) : null}
              </>
            ) : (
              'Median forward path across matched sessions'
            )}
          </p>
        </div>
        {stats.best && stats.worst ? (
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-md bg-emerald-50 px-2 py-1 text-emerald-800">
              Best 5d: {formatDateShort(stats.best.date)} {formatPct(stats.best.value)}
            </span>
            <span className="rounded-md bg-rose-50 px-2 py-1 text-rose-800">
              Worst 5d: {formatDateShort(stats.worst.date)} {formatPct(stats.worst.value)}
            </span>
          </div>
        ) : null}
      </div>

      {chartData.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="h-36 w-full">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              {medianBarTitle}
            </p>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`}
                  domain={[
                    (dataMin: number) => Math.min(dataMin, -0.5),
                    (dataMax: number) => Math.max(dataMax, 0.5),
                  ]}
                />
                <ReferenceLine y={0} stroke="#e5e7eb" />
                <Tooltip content={<MedianBarTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="median" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={barColor(entry.median)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Each analog · 5d forward return
            </p>
            <div className="space-y-1.5">
              {stats.distribution.map((row) => {
                const widthPct = Math.min(100, (Math.abs(row.value) / maxAbs) * 100);
                const positive = row.value >= 0;
                return (
                  <div key={row.date} className="flex items-center gap-2">
                    <span className="w-16 shrink-0 text-[10px] text-gray-500">
                      {formatDateShort(row.date).replace(/, \d{4}$/, '')}
                    </span>
                    <div className="relative h-5 min-w-0 flex-1 rounded bg-gray-100">
                      <div
                        className={`absolute top-0 h-full rounded ${
                          positive ? 'left-1/2 bg-emerald-500/80' : 'right-1/2 bg-rose-500/80'
                        }`}
                        style={{ width: `${widthPct / 2}%` }}
                      />
                      <div className="absolute left-1/2 top-0 h-full w-px bg-gray-300" />
                    </div>
                    <span
                      className={`w-12 shrink-0 text-right text-[10px] font-medium ${pctChipClass(row.value)}`}
                    >
                      {formatPct(row.value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {stats.horizons.length > 1 ? (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
          {stats.horizons.map((h) => (
            <div
              key={h.key}
              className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-center"
            >
              <p className="text-[10px] uppercase tracking-wide text-gray-400">{h.short}</p>
              <p className={`text-sm font-semibold ${pctChipClass(h.median)}`}>
                {formatPct(h.median)}
              </p>
              <p className="text-[10px] text-gray-500">
                {h.positive}/{h.total} up
              </p>
            </div>
          ))}
        </div>
      ) : null}

      {referenceDate ? (
        <AnalogDailyOutlookTimeline
          lens={lens}
          referenceDate={referenceDate}
          subjectLabel={subjectLabel}
        />
      ) : null}
    </div>
  );
}
